import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { apiFetch } from '../../lib/api-client'

interface Report {
  id: string
  type: 'indflytning' | 'fraflytning'
  status: string
  inspection_date: string | null
  property_address: string | null
  property_city: string | null
  unit_no: string | null
  created_at: string
}

const TYPE_LABEL: Record<string, string> = {
  indflytning: 'Indflytningsrapport',
  fraflytning: 'Fraflytningsrapport',
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft:     { label: 'Kladde',       color: '#6b7280', bg: '#f3f4f6' },
  completed: { label: 'Afsluttet',    color: '#16a34a', bg: '#f0fdf4' },
  signed:    { label: 'Underskrevet', color: '#4f46e5', bg: '#eef2ff' },
}

function TypeIcon({ type }: { type: string }) {
  if (type === 'indflytning') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9,22 9,12 15,12 15,22" />
      </svg>
    )
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5,9 2,12 5,15" />
      <path d="M2 12h15" />
      <path d="M8 3h13v18H8" />
    </svg>
  )
}

export function InspectionListPage() {
  const navigate = useNavigate()
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [createType, setCreateType] = useState<'indflytning' | 'fraflytning'>('indflytning')

  useEffect(() => {
    apiFetch<Report[]>('/api/v1/inspection-reports')
      .then(setReports)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleCreate = async () => {
    setCreating(true)
    try {
      const res = await apiFetch<{ id: string }>('/api/v1/inspection-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: createType }),
      })
      navigate(`/flyttesyn/${res.id}`)
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Slet flyttesyn? Dette kan ikke fortrydes.')) return
    try {
      await apiFetch(`/api/v1/inspection-reports/${id}`, { method: 'DELETE' })
      setReports((prev) => prev.filter((r) => r.id !== id))
    } catch {
      alert('Kunne ikke slette flyttesynet.')
    }
  }

  return (
    <AppLayout pageTitle="Flyttesyn">
      <div style={{ maxWidth: 860 }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', marginBottom: 28,
        }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.3px' }}>
              Flyttesyn
            </h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>
              Indflytnings- og fraflytningsrapporter
            </p>
          </div>
          <button
            onClick={() => setShowCreate(v => !v)}
            style={{
              padding: '9px 18px',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Ny rapport
          </button>
        </div>

        {/* Create type picker */}
        {showCreate && (
          <div style={{
            background: 'white',
            border: '1px solid #e8eaed',
            borderRadius: 10,
            padding: '20px 24px',
            marginBottom: 20,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 14 }}>
              Vælg rapporttype
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
              {(['indflytning', 'fraflytning'] as const).map(type => (
                <div
                  key={type}
                  onClick={() => setCreateType(type)}
                  style={{
                    flex: 1,
                    padding: '14px 16px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    border: `1.5px solid ${createType === type ? '#4f46e5' : '#e8eaed'}`,
                    background: createType === type ? '#eef2ff' : '#fafafa',
                    transition: 'border-color .12s',
                  }}
                >
                  <div style={{
                    fontSize: 13, fontWeight: 600,
                    color: createType === type ? '#4f46e5' : '#374151',
                    marginBottom: 3,
                  }}>
                    {type === 'indflytning' ? 'Indflytningsrapport' : 'Fraflytningsrapport'}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    {type === 'indflytning'
                      ? 'Dokumenter boligens stand ved indflytning'
                      : 'Dokumenter boligens stand ved fraflytning'}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{
                  padding: '9px 20px',
                  background: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: creating ? 'default' : 'pointer',
                  opacity: creating ? 0.7 : 1,
                }}
              >
                {creating ? 'Opretter…' : 'Opret rapport'}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                style={{
                  padding: '9px 18px',
                  background: 'white',
                  color: '#6b7280',
                  border: '1px solid #e8eaed',
                  borderRadius: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Annuller
              </button>
            </div>
          </div>
        )}

        {/* Report list */}
        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: 13 }}>Indlæser…</p>
        ) : reports.length === 0 ? (
          <div style={{
            background: 'white',
            border: '1px solid #e8eaed',
            borderRadius: 10,
            padding: '56px 24px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: '#f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
              color: '#9ca3af',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
              </svg>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
              Ingen rapporter endnu
            </div>
            <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
              Opret din første indflytnings- eller fraflytningsrapport
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {reports.map(r => {
              const sc = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.draft
              return (
                <div
                  key={r.id}
                  onClick={() => navigate(`/flyttesyn/${r.id}`)}
                  style={{
                    background: 'white',
                    border: '1px solid #e8eaed',
                    borderRadius: 10,
                    padding: '14px 18px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    transition: 'box-shadow .12s, border-color .12s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#4f46e5'
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(59,91,219,0.10)'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#e8eaed'
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Icon chip */}
                    <div style={{
                      width: 34, height: 34,
                      borderRadius: 8,
                      background: r.type === 'indflytning' ? '#eef2ff' : '#f0fdf4',
                      color: r.type === 'indflytning' ? '#4f46e5' : '#16a34a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <TypeIcon type={r.type} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {TYPE_LABEL[r.type]}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
                        {r.property_address ?? r.unit_no ?? 'Ikke tilknyttet lejemål'}
                        {r.property_city && `, ${r.property_city}`}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {r.inspection_date && (
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>
                        {new Date(r.inspection_date).toLocaleDateString('da-DK')}
                      </span>
                    )}
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '3px 9px',
                      borderRadius: 99,
                      background: sc.bg,
                      color: sc.color,
                    }}>
                      {sc.label}
                    </span>
                    {r.status === 'draft' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(r.id) }}
                        title="Slet rapport"
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 4,
                          cursor: 'pointer',
                          color: '#9ca3af',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 4,
                          lineHeight: 1,
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#ef4444' }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9,18 15,12 9,6" />
                    </svg>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
